/**
 * @fileoverview Refresh token interceptor that handles token refresh and request replay
 * when receiving 401 unauthorized responses.
 */

import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { attemptTokenRefresh } from '@/lib/api/auth-refresh';

const REFRESH_PATH = '/auth/refresh';

const isRefreshRequest = (config?: InternalAxiosRequestConfig) =>
  config?.url?.includes(REFRESH_PATH) ?? false;

type ReplayableConfig = InternalAxiosRequestConfig & { replayedAfterRefresh?: boolean };

/**
 * Interceptor that handles token refresh when receiving 401 unauthorized responses.
 * It ensures that only one token refresh happens at a time and replays failed requests
 * after successful token refresh.
 */
export const refreshTokenInterceptor = async (error: AxiosError, axiosInstance: AxiosInstance) => {
  if (error.response?.status !== 401) {
    throw error;
  }

  // A rejected refresh must not trigger another refresh. The refresh call travels on
  // the same axios instance this interceptor is attached to, so handling its 401 here
  // re-enters attemptTokenRefresh, which then awaits the very request it was re-entered
  // from. That self-await settles nothing: the original request hangs instead of
  // rejecting, so the login redirect never fires and the app never mounts.
  if (isRefreshRequest(error.config)) {
    throw error;
  }

  // A service that refuses a session the platform keeps refreshing would otherwise loop
  // refresh -> replay -> 401 forever, never settling the request or sending the user anywhere.
  if ((error.config as ReplayableConfig | undefined)?.replayedAfterRefresh) {
    throw error;
  }

  const refreshed = await attemptTokenRefresh();
  if (!refreshed) {
    throw error;
  }

  if (!error.config) {
    throw error;
  }

  const replay: ReplayableConfig = { ...error.config, replayedAfterRefresh: true };
  return await axiosInstance.request(replay);
};
